import { Component } from '@angular/core';
import { IFlujoLightTX, IFlujoLightTXDecision, IFlujoLightTXFilters, IFlujoLightTXQueryParams, IFlujoLightTXTransactionStatus } from '../../../interface/IFlujoLightTx';
import { FormBuilder, FormControl } from '@angular/forms';
import { FlujoCreditoLightService } from '../../../shared/services/flujo-credito-light.service';
import { FlujoCreditoLightStatusTX } from 'src/app/enum/EFlujoCreditoLight';
import { Router } from '@angular/router';
import { AlertService } from 'src/app/components/_alert/alert.service';
import { goToTop } from '../utils/utils';
import { SessionService } from 'src/app/shared/services/session.service';
import { ESystemAccess } from 'src/app/enum/EAccess';

@Component({
    selector: 'app-flujo-credito-light',
    templateUrl: './flujo-credito-light.component.html',
    styleUrls: ['./flujo-credito-light.component.scss']
})
export class FlujoCreditoLightComponent {

    private canAccessFlujoCreditoLight = false;
    private hasPermissionToStartFlow = false;

    dataSource: IFlujoLightTX[] = [];

    displayedColumns: string[] = [
        'rut',
        'nombre',
        'estado',
        'fecha',
        'idTransaccion',
        'decision',
        'auto',
        'estaCerrada',
        'acciones',
    ];

    hasUserAccess = true;

    public filtersForm = this.fb.group({
        idTransaccion: [''],
        rut: [''],
        transactionStatus: [null],
        name: [''],
        isAutomaticDecision: [null],
        decision: [null],
        isClosed: [null],
        dateReport: new FormControl<Date | null>(null),
    });

    STATUS_TX_LABELS: Record<FlujoCreditoLightStatusTX, string> = {
        [FlujoCreditoLightStatusTX.COMPLETED]: 'Completado',
        [FlujoCreditoLightStatusTX.IN_PROGRESS]: 'En progreso',
        [FlujoCreditoLightStatusTX.ERROR]: 'Error',
    };

    DECISIONS: Record<string, string> = {
        APROBADO: 'APROBADO',
        RECHAZADO: 'RECHAZADO',
    };

    transactionStatuses: IFlujoLightTXTransactionStatus[] = [];
    decisions: IFlujoLightTXDecision[] = [];

    queryParams: IFlujoLightTXQueryParams = {};
    isLoadingResults = false;
    hasMoreData = true;

    currentModel = '';
    modelNotFound = false;

    isFilterActive = false;


    constructor(
        private fb: FormBuilder,
        private flujoCreditoLightService: FlujoCreditoLightService,
        private router: Router,
        private alertService: AlertService,
        private _sessionService: SessionService
    ) { }

    ngOnInit(): void {

        this.canAccessFlujoCreditoLight = this.setCanAccessFlujoCreditoLight();

        if (!this.canAccessFlujoCreditoLight) {
            this.router.navigate(['/request-access']);
            return;
        }

        this.hasPermissionToStartFlow = this.setHasPermissionToStartFlow();

        this.currentModel = this.flujoCreditoLightService.getModelo() || '';
        this.modelNotFound = this.flujoCreditoLightService.getModeloNotFound();

        if (this.modelNotFound) {
            this.alertService.error('Modelo no encontrado');
        }

        if (!this.currentModel) {
            this.router.navigate(['/flujo-credito-light/seleccion-modelo']);
            return;
        }

        this.getDecisions();
        this.getTransactionsStatus();
        this.getTransactions();

        this.filtersForm.valueChanges.subscribe((changes: any) => {
            const filledKey = Object.keys(changes).find(key => !!changes[key]);
            
            this.isFilterActive = false;

            if (filledKey) {
                Object.keys(this.filtersForm.controls).forEach(key => {
                    const control = this.filtersForm.get(key);
                    if (key === filledKey) {
                        control?.enable({ emitEvent: false }); // Asegura que el campo en uso esté habilitado
                    } else {
                        control?.disable({ emitEvent: false });
                    }
                });
            } else {
                // Si todos los campos están vacíos, se habilitan todos
                Object.keys(this.filtersForm.controls).forEach(key => {
                    this.filtersForm.get(key)?.enable({ emitEvent: false });
                });
            }
        });
    }

    setCanAccessFlujoCreditoLight () {
        const userAccess = this._sessionService.getUserAccess();

        const flujoCreditoLightPermission = [
            ESystemAccess.FLUJO_CREDITO_LIGHT,
            ESystemAccess.FLUJO_CREDITO_LIGHT_ADMIN,
            ESystemAccess.FLUJO_CREDITO_LIGHT_BUSCAR
        ]

        return flujoCreditoLightPermission.some(permission => userAccess.includes(permission));
    }

    setHasPermissionToStartFlow () {
        const userAccess = this._sessionService.getUserAccess();

        const flujoCreditoLightPermission = [
            ESystemAccess.FLUJO_CREDITO_LIGHT,
            ESystemAccess.FLUJO_CREDITO_LIGHT_ADMIN,
        ]

        return flujoCreditoLightPermission.some(permission => userAccess.includes(permission));
    }

    getStatusLabel(estado: string): string {
        return this.STATUS_TX_LABELS[estado as FlujoCreditoLightStatusTX] || estado;
    }

    getTransactions(): void {
        this.isLoadingResults = true;
        this.queryParams.limit = '40'; // pasar a variable
        this.queryParams.model = this.currentModel;
        this.flujoCreditoLightService.getTransactions(this.queryParams).subscribe(
            response => {
                this.dataSource = [...this.dataSource, ...response.transactions];
                this.queryParams.encodedCursor = response.encodedCursor;
                this.isLoadingResults = false;
                this.hasMoreData = !!response.encodedCursor
            },
            error => {
                this.alertService.error(`Error al obtener transacciones: ${error?.error?.message}`);
                goToTop();
                this.isLoadingResults = false;
            }
        );
    }

    applyFilters(): void {
        const [filterKey, filterValue] = Object.entries(this.filtersForm.value)[0];

        if (!filterValue) {
            this.clearFilters();
            return;
        }

        this.resetScroll();

        let formattedDate = null;
        if (filterKey === 'dateReport' && filterValue instanceof Date) {
            formattedDate = filterValue.toISOString().split('T')[0];
        }

        this.queryParams = {
            filterKey,
            filterValue: formattedDate ? formattedDate : filterValue.toString().trim(),
        }
        
        this.isFilterActive = true;
        this.getTransactions()
    }

    clearFilters(): void {
        this.isFilterActive = false;
        this.filtersForm.reset();
        this.resetScroll();
        this.getTransactions();
    }

    getTransactionsStatus(): void {
        this.flujoCreditoLightService.getTransactionsStatuses().subscribe(
            statuses => {
                this.transactionStatuses = statuses.map(status => ({
                    key: status.key,
                    displayName: this.STATUS_TX_LABELS[status.key as FlujoCreditoLightStatusTX] || status.key
                }));
            },
            error => {
                this.transactionStatuses = [];
                this.alertService.error(`Error al obtener estados de transacciones: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    getDecisions(): void {
        this.flujoCreditoLightService.getDecisions(this.currentModel).subscribe(
            decisions => {
                this.decisions = Array.isArray(decisions) ? decisions : [];
            },
            error => {
                this.decisions = [];
                this.alertService.error(`Error al obtener decisiones: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    onTableScroll(event: any): void {
        const scrollTop = event.target.scrollTop;
        const scrollHeight = event.target.scrollHeight;
        const offsetHeight = event.target.offsetHeight;

        if (scrollHeight - (scrollTop + offsetHeight) < 50 && !this.isLoadingResults && this.hasMoreData) {
            this.getTransactions();
        }
    }

    resetScroll(): void {
        this.dataSource = [];
        this.queryParams = {};
        this.hasMoreData = true;
    }

    canViewClientCard(element: IFlujoLightTX): boolean {
        return element.estado === FlujoCreditoLightStatusTX.COMPLETED && (element.auto || element.estaCerrada);
    }

    canStartApprovalFlow(element: IFlujoLightTX): boolean {
        if (!this.hasPermissionToStartFlow) return false;

        return element.estado === FlujoCreditoLightStatusTX.COMPLETED && !element.auto && !element.estaCerrada
    }

    getDecisionBackgroundColorClass(decision: string): string {
        const decisionUpperCase = decision.toUpperCase();

        if (decisionUpperCase === this.DECISIONS.APROBADO) return 'bg-color-completed';
        
        if (decisionUpperCase === this.DECISIONS.RECHAZADO) return 'bg-color-error';

        return 'bg-color-in-progress';
    }

    viewClientCard(element: IFlujoLightTX): void {
        this.router.navigate([`/flujo-credito-light/${element.idTransaccion}/ficha-cliente`]);
    }
}